import { Network } from 'cloud-networks/modules/networksModule/NetworkModule.types';
import { Port } from 'cloud-networks/modules/PortsModule/PortsModule.types';
import { Router } from 'cloud-networks/modules/RoutersModule/RoutersModule.types';
import { SubnetApiEntity } from 'cloud-networks/modules/SubnetModule/SubnetModule.types';
import { SubnetUtils } from 'cloud-networks/modules/SubnetModule/SubnetUtils';
import * as R from 'ramda';
import React from 'react';
import { JsonSafe } from 'utils/JsonSafe';

import { Environment } from 'Environment';
import { BoundWhiteLabelSelectors } from 'reactApp/modules/WhiteLabelModule/WhiteLabelModule';
import { DropdownOption } from 'reactComponents/Dropdown/Dropdown';
import { ToastNotify } from 'reactComponents/ToastNotify/ToastNotify';
import { Tooltip } from 'reactComponents/Tooltip/Tooltip';

const INT_NET_NAME = Environment.isProductionHost ? 'int-net1' : 'int-net';

const DEFAULT_INT_SUBNET_IDS = Environment.isProductionHost
    ? ['af7e6915-4dbb-4868-924e-bffde931c02a']
    : ['7db7de9b-ad86-4ffc-8e53-04bd6ecdbf59'];

const DEFAULT_INT_NET_IDS = Environment.isProductionHost
    ? [
          // int-net1
          '19fcebaa-5b40-445e-837d-2e751579a2b1',
      ]
    : [
          // int-net
          '8e383f1e-2cb2-4477-a102-7d5eca5a66e6',
      ];

const DEFAULT_EXT_NET_IDS = Environment.isProductionHost
    ? ['298117ae-3fa4-4109-9e08-8be5602be5a2']
    : ['31a7e1eb-9433-4372-935a-7566f1fdbf4b'];

const getIntNetName = () => INT_NET_NAME;

const getSubnetsWithRouterConnectivity = ({
    networks,
    ports,
    routers,
    subnets,
}: {
    networks: Network[];
    subnets: SubnetApiEntity[];
    routers: Router[];
    ports: Port[];
    // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
    const externalNetworks = filterExternal(networks);
    const subnetsWithRouterConnectivity: SubnetApiEntity[] = [];

    for (const subnet of subnets) {
        for (const port of ports) {
            for (const fixed_ips of port.fixed_ips) {
                for (const router of routers) {
                    for (const externalNetwork of externalNetworks) {
                        if (
                            subnet.id === fixed_ips.subnet_id &&
                            port.device_id === router.id &&
                            router.external_gateway_info &&
                            router.external_gateway_info.network_id ===
                                externalNetwork.id &&
                            !subnetsWithRouterConnectivity.includes(subnet)
                        ) {
                            subnetsWithRouterConnectivity.push(subnet);
                        }
                    }
                }
            }
        }
    }

    return subnetsWithRouterConnectivity;
};

const getInternalNetworksWithExtrenalConnectivity = ({
    networks,
    subnets,
    routers,
    ports,
}) => {
    const internalNetworks = filterInternal(networks);

    return internalNetworks.filter((internalNetwork) =>
        hasInternalNetworkExternalConnection({
            internalNetwork,
            networks,
            subnets,
            routers,
            ports,
        }),
    );
};

const hasInternalNetworkExternalConnection = ({
    internalNetwork,
    networks,
    ports,
    routers,
    subnets,
    subnetIdsOfServerPorts,
}: {
    internalNetwork: Network;
    networks: Network[];
    subnets: SubnetApiEntity[];
    routers: Router[];
    ports: Port[];
    subnetIdsOfServerPorts?: string[];
}) => {
    const { subnets: subnetIds } = internalNetwork;

    if (!subnetIds.length) {
        return routers.some(
            R.pathEq(
                ['external_gateway_info', 'network_id'],
                internalNetwork.id,
            ),
        );
    }

    const allExternalSubnets = getSubnetsWithRouterConnectivity({
        networks,
        subnets,
        routers,
        ports,
    });

    const connectedSubnetIds = subnetIds.filter((subnetId) =>
        allExternalSubnets.find((subnet) => subnet.id === subnetId),
    );

    return subnetIdsOfServerPorts
        ? R.intersection(connectedSubnetIds, subnetIdsOfServerPorts).length
        : connectedSubnetIds.length === subnetIds.length;
};

const getConnectedExtNetsOfPrivateNets = ({
    privateNetworks,
    networks,
    ports,
    routers,
}: {
    privateNetworks: Network[];
    networks: Network[];
    ports: Port[];
    routers: Router[];
}) => {
    const privateNetIds = privateNetworks.map((item) => item.id);
    const connectedPorts = ports.filter((item) =>
        privateNetIds.includes(item.network_id),
    );
    const conectedRouterIds = connectedPorts.map((item) => item.device_id);
    const conectedRouters = routers.filter((item) =>
        conectedRouterIds.includes(item.id),
    );
    const externalNetworkIds = R.uniq(
        conectedRouters.flatMap(
            (item) => item.external_gateway_info?.network_id || [],
        ),
    );

    return networks.filter((item) => externalNetworkIds.includes(item.id));
};

const hasExternalConnection = ({
    network,
    networks,
    subnets,
    routers,
    ports,
    serverId,
}: {
    serverId: string;
    network: Network;
    networks: Network[];
    ports: Port[];
    routers: Router[];
    subnets: SubnetApiEntity[];
}) => {
    if (isExternalNetwork(network)) {
        return true;
    }

    const serverPorts = ports.filter((port) => port?.device_id === serverId);
    const subnetIdsOfServerPorts = SubnetUtils.getListByPorts(
        subnets,
        serverPorts,
    ).map((item) => item.id);

    return hasInternalNetworkExternalConnection({
        internalNetwork: network,
        networks,
        subnets,
        routers,
        ports,
        subnetIdsOfServerPorts,
    });
};

// getNetworksOptions - возвращает сети с выходом в ext-net.
// Считаем, что сеть имеет выход на ext-net, если все её подсети связаны с роутером,
// у которого есть выход на ext-net
const getNetworksOptions = ({
    includeDefaultInternalNetworks = false,
    noInternetNetworksAvailable = false,
    defaultOptions,
    networks,
    subnets,
    routers,
    ports,
}: {
    includeDefaultInternalNetworks?: boolean;
    noInternetNetworksAvailable?: boolean;
    defaultOptions?: DropdownOption[];
    networks: Network[];
    subnets: SubnetApiEntity[];
    routers: Router[];
    ports: Port[];
}) => {
    const options = [...(defaultOptions || [])];

    const internalNetworksWithExtrenalConnectivity = getInternalNetworksWithExtrenalConnectivity(
        { networks, subnets, routers, ports },
    );

    const availableNetworks = noInternetNetworksAvailable
        ? networks
        : internalNetworksWithExtrenalConnectivity;

    const availableNetworksIds = availableNetworks.map(({ id }) => id);

    if (includeDefaultInternalNetworks) {
        availableNetworksIds.unshift(...DEFAULT_INT_NET_IDS);
    }

    const filteredNetworks = includeDefaultInternalNetworks
        ? filterInternal(networks)
        : filterInternal(networks).filter(
              (network) => !DEFAULT_INT_NET_IDS.includes(network.id),
          );

    for (const network of filteredNetworks) {
        const isAvailableNetwork = availableNetworksIds.includes(network.id);

        if (isAvailableNetwork) {
            options.push({
                key: network.id,
                value: network.id,
                text: network.name,
                disabled: false,
            });
        } else {
            options.push({
                key: network.id,
                value: network.id,
                disabled: true,
                content: (
                    <Tooltip
                        trigger={
                            <div
                                style={{ pointerEvents: 'auto' }}
                                onClick={(event) => event.stopPropagation()}
                            >
                                {network.name}
                            </div>
                        }
                        content="Чтобы выбрать данную сеть, создайте маршрутизатор к сети ext-net"
                        position="right center"
                        basic
                    />
                ),
            });
        }
    }

    return R.sortBy(R.prop<any>('disabled'), options);
};

const getFirstAvailableNetworkId = ({
    noInternetNetworksAvailable = false,
    networks,
    subnets,
    routers,
    ports,
}) => {
    const networksOptions = getNetworksOptions({
        noInternetNetworksAvailable,
        networks,
        subnets,
        routers,
        ports,
    });

    return (
        (networksOptions.find((option) => !option.disabled)?.value as string) ||
        'new'
    );
};

const isExternalNetwork = (network) => Boolean(network?.['router:external']);
const isInternalNetwork = (network) => !isExternalNetwork(network);
const filterExternal = R.filter(isExternalNetwork);
const filterInternal = R.reject(isExternalNetwork);

const isDefaultInternalNetwork = (networkId) => {
    return DEFAULT_INT_NET_IDS.includes(networkId);
};

const isNetworkEditable = (network) => {
    return (
        networksUtils.isInternalNetwork(network) &&
        !networksUtils.isDefaultInternalNetwork(network.id) &&
        !networksUtils.isExternalNetwork(network)
    );
};

const getRouterWithExtInterface = ({ routersList }) => {
    return routersList.find((router) => router.external_gateway_info !== null);
};

const getSuitableSubNetworks = (networksOptions) => {
    return networksOptions
        .filter(
            (option) =>
                !option.disabled &&
                Array.isArray(option.items) &&
                option.items.find((item) => !item.disabled),
        )
        .reduce((acc, option) => acc.concat(option.items), []);
};

const getFirstSuitableSubNetwork = (networksOptions) => {
    const subnets = getSuitableSubNetworks(networksOptions);
    const subnet = subnets.find((item) => !item.disabled);

    return subnet ? subnet.value : 'new';
};

const hasOnlySingleSubNetwork = (networksOptions) => {
    const subnets = getSuitableSubNetworks(networksOptions);

    return subnets.length === 1;
};

const getFirstAvailableNetworkOrSubnetId = ({
    subnets,
    externalNetworkList,
}) => {
    const availableSubnets = subnets.filter(
        (item) =>
            !DEFAULT_INT_NET_IDS.includes(item.network_id) &&
            !DEFAULT_EXT_NET_IDS.includes(item.network_id),
    );

    if (availableSubnets[0]) {
        return availableSubnets[0].id;
    }
    return externalNetworkList[0].id;
};

const getNetworkName = (network) => {
    const name = network?.name || network?.id;
    if (isExternalNetwork(network)) {
        return `Внешняя сеть (${name})`;
    }
    return name;
};

const getNetworkByNetOrSubnetId = ({ netOrSubnetId, networks, subnets }) => {
    const subnet = subnets.find((item) => item.id === netOrSubnetId);
    const network = networks.find(
        (item) => item.id === subnet?.network_id || netOrSubnetId,
    );

    return {
        subnet,
        network,
        networkId: network?.id || netOrSubnetId,
    };
};

const notifyOverlapsError = (e, cidr) => {
    if (!e.responseText) {
        return;
    }
    const error = JsonSafe.parse(e.responseText);

    if (!error?.NeutronError?.message) {
        return;
    }
    const regex = /Bad router request: Cidr [\d\w\-./]+ of subnet [\d\w\-./]+ overlaps with cidr [\d\w\-./]+ of subnet [\d\w\-./А-Яа-яёЁ]+./;

    if (regex.test(error.NeutronError.message)) {
        ToastNotify.error(
            `Невозможно подключить подсеть ${cidr}, так как диапазон адресов принадлежит другой подсети`,
        );
    }
};

const isDefaultExtNet = (networkId) => {
    return DEFAULT_EXT_NET_IDS.includes(networkId);
};

const getDefaultDnsDomain = () => {
    if (!Environment.isPrivateBuild) {
        return 'mcs.local';
    }
    const whiteLabelSettings = BoundWhiteLabelSelectors.getWhiteLabelSettings();
    return whiteLabelSettings?.defaultDnsDomain;
};

export const networksUtils = {
    notifyOverlapsError,
    getIntNetName,
    getNetworksOptions,
    getFirstAvailableNetworkId,
    getFirstAvailableNetworkOrSubnetId,
    DEFAULT_INT_SUBNET_IDS,
    DEFAULT_INT_NET_IDS,
    DEFAULT_EXT_NET_IDS,
    getRouterWithExtInterface,
    isDefaultInternalNetwork,
    isExternalNetwork,
    isInternalNetwork,
    isNetworkEditable,
    getFirstSuitableSubNetwork,
    hasOnlySingleSubNetwork,
    hasExternalConnection,
    getNetworkName,
    hasInternalNetworkExternalConnection,
    getSubnetsWithRouterConnectivity,
    filterInternal,
    getNetworkByNetOrSubnetId,
    getConnectedExtNetsOfPrivateNets,
    isDefaultExtNet,
    getDefaultDnsDomain,
};
